import { useRef, useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { alpha } from '@mui/material/styles';
import { NET_QUALITY_CONFIG, STATUS_CONFIG } from '../constants';
import { getCoords, getStatus } from '../utils';
import { API_BASE } from '../constants';

/**
 * Manages the Leaflet map lifecycle:
 *   - CDN script loading
 *   - Map initialisation (tiles, zoom control, CSS keyframes)
 *   - Clustered user markers (updated when `filtered` rows change)
 *   - Trail rendering (polyline + ping dots + avatar endpoint markers)
 *   - Marker opacity fading when a user is selected
 *
 * Returns all refs and state needed by ActivityPage and MapSidePanel.
 */
export function useMap({ filtered, token, onMarkerClickRef }) {
  const mapRef            = useRef(null);   // DOM node
  const mapInstance       = useRef(null);   // L.Map
  const leafletRef        = useRef(null);   // window.L
  const clusterGroupRef   = useRef(null);
  const markersRef        = useRef({});     // rowId → L.Marker
  const userIdToRowIdRef  = useRef({});

  const trailLayersRef    = useRef([]);
  const trailPolylineRef  = useRef(null);
  const lastTrailDotRef   = useRef(null);
  const activityMarkersRef = useRef([]);

  const [leafletReady,   setLeafletReady]   = useState(false);
  const [activeTrailRow, setActiveTrailRow] = useState(null);
  const [panelVisible,   setPanelVisible]   = useState(false);
  const [trail,          setTrail]          = useState([]);
  const [trailLoading,   setTrailLoading]   = useState(false);

  const activeTrailRowRef = useRef(null);
  const trailRef          = useRef([]);

  // Keep refs in sync
  useEffect(() => { trailRef.current = trail; }, [trail]);
  useEffect(() => { activeTrailRowRef.current = activeTrailRow; }, [activeTrailRow]);

  // ── Load Leaflet + MarkerCluster from CDN ─────────────────────────────────
  useEffect(() => {
    if (window.L && window.L.markerClusterGroup) {
      leafletRef.current = window.L;
      setLeafletReady(true);
      return;
    }

    const addLink = (href) => {
      const el = document.createElement('link');
      el.rel = 'stylesheet';
      el.href = href;
      document.head.appendChild(el);
    };

    addLink('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css');
    addLink('https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css');
    addLink('https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css');

    const leafletScript = document.createElement('script');
    leafletScript.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    leafletScript.onload = () => {
      const clusterScript = document.createElement('script');
      clusterScript.src = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js';
      clusterScript.onload = () => {
        leafletRef.current = window.L;
        setLeafletReady(true);
      };
      document.head.appendChild(clusterScript);
    };
    document.head.appendChild(leafletScript);
  }, []);

  // ── Initialise map once Leaflet is ready ──────────────────────────────────
  useEffect(() => {
    if (!leafletReady || !mapRef.current || mapInstance.current) return;
    const L = leafletRef.current;

    mapInstance.current = L.map(mapRef.current, {
      center: [14.5995, 120.9842], zoom: 11, zoomControl: false,
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap &copy; CARTO', maxZoom: 19,
    }).addTo(mapInstance.current);

    L.control.zoom({ position: 'bottomright' }).addTo(mapInstance.current);

    // Inject CSS keyframes and Leaflet popup overrides
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInLeft {
        from { transform: translateX(-100%); opacity: 0.6; }
        to   { transform: translateX(0);     opacity: 1;   }
      }
      @keyframes slideOutLeft {
        from { transform: translateX(0);     opacity: 1;   }
        to   { transform: translateX(-100%); opacity: 0;   }
      }
      @keyframes pulseNew {
        0%   { transform: scale(1);   box-shadow: 0 0 0 0 rgba(16,117,186,0.6); }
        50%  { transform: scale(1.6); box-shadow: 0 0 0 8px rgba(16,117,186,0); }
        100% { transform: scale(1);   box-shadow: 0 0 0 0 rgba(16,117,186,0); }
      }
      @keyframes blink {
        0%, 100% { opacity: 1;   }
        50%       { opacity: 0.3; }
      }
      .leaflet-popup-content-wrapper {
        background: #ffffff !important;
        border: 1px solid rgba(16,117,186,0.2) !important;
        border-radius: 14px !important;
        box-shadow: 0 8px 32px rgba(15,33,55,0.15) !important;
        padding: 0 !important; overflow: hidden !important;
      }
      .leaflet-popup-tip     { background: #ffffff !important; }
      .leaflet-popup-content { margin: 0 !important; }
      .leaflet-container     { font-family: "DM Sans", sans-serif !important; }
      .custom-marker { cursor: pointer !important; }
      .custom-marker div { transition: transform 0.2s; }
      .custom-marker:hover div { transform: scale(1.12) !important; }
      .trail-popup .leaflet-popup-content-wrapper { border-radius: 10px !important; box-shadow: 0 4px 20px rgba(15,33,55,0.18) !important; }
      .marker-cluster, .marker-cluster div { background: transparent !important; border: none !important; box-shadow: none !important; }
    `;
    document.head.appendChild(style);
  }, [leafletReady]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const clearTrailLayers = useCallback(() => {
    if (!mapInstance.current) return;
    trailLayersRef.current.forEach((layer) => { try { mapInstance.current.removeLayer(layer); } catch { } });
    trailLayersRef.current = [];
  }, []);

  const clearActivityMarkers = useCallback(() => {
    if (!mapInstance.current) return;
    activityMarkersRef.current.forEach((m) => { try { mapInstance.current.removeLayer(m); } catch { } });
    activityMarkersRef.current = [];
  }, []);

  const setMarkersOpacity = useCallback((opacity, exceptRowId) => {
    Object.entries(markersRef.current).forEach(([rowId, marker]) => {
      if (marker.setOpacity) {
        marker.setOpacity(exceptRowId && rowId === String(exceptRowId) ? 1 : opacity);
      }
    });

    const cg = clusterGroupRef.current;
    if (!cg) return;

    if (opacity < 1 && exceptRowId) {
      const selectedMarker = markersRef.current[String(exceptRowId)];
      const visibleParent  = selectedMarker ? cg.getVisibleParent(selectedMarker) : null;

      cg._featureGroup.eachLayer((layer) => {
        const el = layer.getElement?.();
        if (!el) return;
        if (layer === selectedMarker || layer === visibleParent) {
          el.style.opacity = '1'; el.style.filter = 'none';
        } else {
          el.style.opacity = String(opacity); el.style.filter = `opacity(${opacity})`;
        }
      });
    } else {
      cg._featureGroup.eachLayer((layer) => {
        const el = layer.getElement?.();
        if (!el) return;
        el.style.opacity = '1'; el.style.filter = 'none';
      });
    }
  }, []);

  // ── Avatar endpoint markers (check-in / check-out) ───────────────────────
  const makeAvatarMarker = useCallback((L, map, lat, lng, row, borderColor, badgeLabel, badgeColor) => {
    const author   = row.author;
    const photo    = row?.checkin?.imageSelfie?.data || author?.photoURL || '';
    const name     = `${author?.firstName || ''} ${author?.lastName || ''}`.trim();
    const isCO     = badgeLabel === 'Check Out';

    const iconHtml =
      '<div style="position:relative;width:50px;height:50px;">' +
      '<div style="width:50px;height:50px;border-radius:50%;border:3px solid ' + borderColor + ';box-shadow:0 4px 14px rgba(15,33,55,0.2),0 0 0 3px ' + borderColor + '33;overflow:hidden;background:#f0f8f4;">' +
      '<img src="' + photo + '" style="width:100%;height:100%;object-fit:cover;" onerror="this.style.display=\'none\';" /></div>' +
      '<div style="position:absolute;bottom:0;right:0;width:14px;height:14px;border-radius:50%;background:' + borderColor + ';border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.2);"></div>' +
      '<div style="position:absolute;top:-20px;left:50%;transform:translateX(-50%);background:' + badgeColor + ';color:#fff;font-size:9px;font-weight:700;font-family:DM Sans,sans-serif;padding:2px 6px;border-radius:8px;white-space:nowrap;box-shadow:0 1px 4px rgba(15,33,55,0.2);border:1.5px solid #fff;">' + badgeLabel + '</div>' +
      '</div>';

    const icon   = L.divIcon({ html: iconHtml, className: '', iconSize: [50, 50], iconAnchor: [25, 25], popupAnchor: [0, -38] });
    const marker = L.marker([lat, lng], { icon, interactive: false }).addTo(map);

    const el = marker.getElement();
    if (el) {
      const selfieData = isCO ? row.checkout?.imageSelfie?.data : row.checkin?.imageSelfie?.data;
      const storeData  = isCO ? row.checkout?.imageStore?.data  : row.checkin?.imageStore?.data;
      const timeRaw    = isCO ? row.checkout?.timeReported      : row.checkin?.timeReported;
      const time       = timeRaw ? format(new Date(timeRaw), 'hh:mm a') : '';

      const popupHtml =
        '<div style="padding:11px 13px;min-width:170px;font-family:DM Sans,sans-serif;">' +
        '<div style="display:flex;align-items:center;gap:8px;">' +
        '<img src="' + photo + '" style="width:34px;height:34px;border-radius:50%;border:2px solid ' + borderColor + ';object-fit:cover;flex-shrink:0;background:#f0f8f4;" onerror="this.style.background=\'' + borderColor + '\';" />' +
        '<div><div style="font-weight:700;font-size:12px;color:#0f2137;">' + name + '</div>' +
        '<div style="font-size:10px;color:' + badgeColor + ';font-weight:700;">' + badgeLabel + (time ? ' · ' + time : '') + '</div></div>' +
        '</div>' +
        ((selfieData || storeData)
          ? '<div style="display:flex;gap:5px;margin-top:7px;">' +
            (selfieData ? '<a href="' + selfieData + '" target="_blank" style="display:block;width:42px;height:42px;border-radius:6px;overflow:hidden;border:1.5px solid ' + borderColor + ';"><img src="' + selfieData + '" style="width:100%;height:100%;object-fit:cover;" /></a>' : '') +
            (storeData  ? '<a href="' + storeData  + '" target="_blank" style="display:block;width:42px;height:42px;border-radius:6px;overflow:hidden;border:1.5px solid ' + borderColor + '60;"><img src="' + storeData  + '" style="width:100%;height:100%;object-fit:cover;" /></a>' : '') +
            '</div>'
          : '') +
        '</div>';

      marker.bindPopup(popupHtml, { maxWidth: 230, closeButton: false, className: 'trail-popup' });
      el.style.pointerEvents = 'auto';
      el.style.cursor = 'default';
      el.addEventListener('mouseover', () => marker.openPopup());
      el.addEventListener('mouseout',  () => marker.closePopup());
    }
    return marker;
  }, []);

  // ── Load and draw trail for a selected row ────────────────────────────────
  const loadTrail = useCallback(async (row, socketRef, fetchTasks) => {
    if (!mapInstance.current || !leafletReady) return;
    const L = leafletRef.current;

    const checkinCode = row.checkin?.code ?? row.checkin?._id;
    if (!checkinCode) { console.warn('[Trail] no checkin code on row'); return; }

    clearTrailLayers();
    clearActivityMarkers();
    setActiveTrailRow(row);
    setTrail([]);
    setTrailLoading(true);
    setPanelVisible(true);
    setMarkersOpacity(0.25, row._id);

    // Subscribe to live pings for this session
    if (socketRef?.current) {
      if (activeTrailRowRef.current) {
        const prevId = activeTrailRowRef.current.checkin?.code ?? activeTrailRowRef.current.checkin?._id;
        if (prevId) socketRef.current.emit('unsubscribe:session', String(prevId));
      }
      socketRef.current.emit('subscribe:session', String(checkinCode));
    }

    let pings = [];
    try {
      const res  = await fetch(`${API_BASE}/api/v1/location/session/${checkinCode}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      pings = (data.data ?? [])
        .filter((p) => p.location?.latitude != null && p.location?.longitude != null)
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    } catch (e) { console.error('[Trail] fetch failed', e); }

    setTrail(pings);
    setTrailLoading(false);

    if (fetchTasks) fetchTasks(row);

    // Collect all geographic points for the polyline
    const checkinCoords  = getCoords(row);
    const checkoutCoords = (() => {
      const co = row.checkout;
      if (!co || co === 'NA') return null;
      const lat = parseFloat(co.lat);
      const lng = parseFloat(co.long ?? co.lng);
      if (isNaN(lat) || isNaN(lng)) return null;
      return { lat, lng };
    })();

    const allPoints = [
      ...(checkinCoords  ? [[checkinCoords.lat,  checkinCoords.lng]]  : []),
      ...pings.map((p) => [p.location.latitude, p.location.longitude]),
      ...(checkoutCoords ? [[checkoutCoords.lat, checkoutCoords.lng]] : []),
    ];

    // Draw polyline
    if (allPoints.length >= 2) {
      const polyline = L.polyline(allPoints, {
        color: '#91ce0f', weight: 3, opacity: 0.75,
        dashArray: '6 4', lineCap: 'round', lineJoin: 'round',
      }).addTo(mapInstance.current);
      trailPolylineRef.current = polyline;
      trailLayersRef.current.push(polyline);
    }

    // Draw ping dots
    pings.forEach((ping, idx) => {
      const netCfg   = NET_QUALITY_CONFIG[ping.deviceContext?.networkQuality ?? 'unknown'];
      const isFirst  = idx === 0;
      const isLast   = idx === pings.length - 1;
      const dotColor = isFirst ? '#0a9e74' : isLast ? '#c06000' : '#91ce0f';
      const dotSize  = isFirst || isLast ? 14 : 10;
      const ts       = ping.timestamp ? format(new Date(ping.timestamp), "MMM d 'at' hh:mm a") : '—';

      const dotIcon = L.divIcon({
        html: `<div style="width:${dotSize}px;height:${dotSize}px;border-radius:50%;background:${dotColor};border:2.5px solid #fff;box-shadow:0 2px 8px rgba(15,33,55,0.25),0 0 0 2px ${dotColor}40;cursor:pointer;transition:transform 0.15s;"></div>`,
        className: '', iconSize: [dotSize, dotSize],
        iconAnchor: [dotSize / 2, dotSize / 2], popupAnchor: [0, -(dotSize / 2 + 6)],
      });

      const appStateLabel = ping.deviceContext?.appState === 'background' ? '📱 Background'
        : ping.deviceContext?.appState === 'active' ? '📱 Active' : '📱 Unknown';
      const lateTag    = ping.isLate ? `<span style="font-size:10px;color:#ef4444;background:#fef2f2;border:1px solid #fecaca;padding:1px 5px;border-radius:4px;font-weight:700;">Late +${ping.latenessMinutes}m</span>` : '';
      const offlineTag = ping.wasOfflineQueued ? `<span style="font-size:10px;color:#f59e0b;background:#fffbeb;border:1px solid #fde68a;padding:1px 5px;border-radius:4px;font-weight:700;">Offline queued</span>` : '';

      const popupHtml = `
        <div style="padding:12px 14px;min-width:200px;font-family:'DM Sans',sans-serif;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px;">
            <div style="display:flex;align-items:center;gap:6px;">
              <div style="width:10px;height:10px;border-radius:50%;background:${dotColor};flex-shrink:0;"></div>
              <span style="font-size:12px;font-weight:700;color:#0f2137;">${isFirst ? 'First ping' : isLast ? 'Last ping' : `Ping #${idx + 1}`}</span>
            </div>
            <div style="display:flex;gap:4px;">${lateTag}${offlineTag}</div>
          </div>
          <div style="padding-top:8px;border-top:1px solid #eef2f7;display:grid;gap:5px;">
            <div style="display:flex;justify-content:space-between;align-items:center;">
              <span style="font-size:11px;color:#6b7f8e;">Time</span>
              <span style="font-size:11px;font-weight:600;color:#0f2137;font-family:monospace;">${ts}</span>
            </div>
            <div style="display:flex;justify-content:space-between;align-items:center;">
              <span style="font-size:11px;color:#6b7f8e;">Coords</span>
              <span style="font-size:10px;color:#4a6a85;font-family:monospace;">${ping.location.latitude.toFixed(5)}, ${ping.location.longitude.toFixed(5)}</span>
            </div>
            <div style="display:flex;justify-content:space-between;align-items:center;">
              <span style="font-size:11px;color:#6b7f8e;">Network</span>
              <span style="font-size:11px;font-weight:600;color:${netCfg.color};">${netCfg.label}</span>
            </div>
            <div style="display:flex;justify-content:space-between;align-items:center;">
              <span style="font-size:11px;color:#6b7f8e;">App state</span>
              <span style="font-size:11px;color:#4a6a85;">${appStateLabel}</span>
            </div>
            ${ping.deviceContext?.connectionType ? `<div style="display:flex;justify-content:space-between;align-items:center;"><span style="font-size:11px;color:#6b7f8e;">Connection</span><span style="font-size:11px;color:#4a6a85;">${ping.deviceContext.connectionType}</span></div>` : ''}
          </div>
        </div>`;

      const dot = L.marker([ping.location.latitude, ping.location.longitude], { icon: dotIcon }).addTo(mapInstance.current);
      dot.bindPopup(popupHtml, { maxWidth: 260, closeButton: false, className: 'trail-popup' });
      dot.on('mouseover', () => dot.openPopup());
      dot.on('mouseout',  () => dot.closePopup());
      trailLayersRef.current.push(dot);
    });

    const dotsOnly = trailLayersRef.current.filter((l) => l !== trailPolylineRef.current);
    lastTrailDotRef.current = dotsOnly[dotsOnly.length - 1] ?? null;

    // Check-in / check-out avatar markers
    if (checkinCoords)  activityMarkersRef.current.push(makeAvatarMarker(L, mapInstance.current, checkinCoords.lat,  checkinCoords.lng,  row, '#0a9e74', 'Check In',  '#0a9e74'));
    if (checkoutCoords) activityMarkersRef.current.push(makeAvatarMarker(L, mapInstance.current, checkoutCoords.lat, checkoutCoords.lng, row, '#c06000', 'Check Out', '#c06000'));

    if (allPoints.length >= 1) {
      try { mapInstance.current.fitBounds(allPoints, { paddingTopLeft: [380, 60], paddingBottomRight: [60, 60], maxZoom: 16 }); } catch { }
    }
  }, [leafletReady, token, clearTrailLayers, clearActivityMarkers, setMarkersOpacity, makeAvatarMarker]);

  // ── Clear trail ───────────────────────────────────────────────────────────
  const clearTrail = useCallback((socketRef, setTasksFn) => {
    if (socketRef?.current && activeTrailRowRef.current) {
      const sessionId = activeTrailRowRef.current.checkin?.code ?? activeTrailRowRef.current.checkin?._id;
      if (sessionId) socketRef.current.emit('unsubscribe:session', String(sessionId));
    }
    setPanelVisible(false);
    setTimeout(() => {
      trailPolylineRef.current = null;
      lastTrailDotRef.current  = null;
      clearTrailLayers();
      clearActivityMarkers();
      setActiveTrailRow(null);
      setTrail([]);
      if (setTasksFn) setTasksFn([]);
      trailRef.current = [];
      setMarkersOpacity(1);
    }, 220);
  }, [clearTrailLayers, clearActivityMarkers, setMarkersOpacity]);

  // ── Clustered user markers (rebuilt when filtered rows change) ────────────
  useEffect(() => {
    if (!mapInstance.current || !leafletReady) return;
    const L   = leafletRef.current;
    const map = mapInstance.current;

    if (clusterGroupRef.current) {
      try { map.removeLayer(clusterGroupRef.current); } catch { }
      clusterGroupRef.current = null;
    }
    markersRef.current        = {};
    userIdToRowIdRef.current  = {};

    const validRows = filtered.filter((r) => getCoords(r));
    if (!validRows.length) return;

    const clusterGroup = L.markerClusterGroup({
      maxClusterRadius: 50,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      animate: true,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div style="width:42px;height:42px;border-radius:50%;background:linear-gradient(135deg,#91ce0f,#5f940a);border:3px solid #fff;box-shadow:0 4px 14px rgba(15,33,55,0.25),0 0 0 3px rgba(16,117,186,0.25);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:13px;font-family:'DM Sans',sans-serif;">${count}</div>`,
          className: '', iconSize: [42, 42], iconAnchor: [21, 21],
        });
      },
    });
    clusterGroupRef.current = clusterGroup;
    map.addLayer(clusterGroup);

    const bounds = [];

    validRows.forEach((row) => {
      const coords = getCoords(row);
      const status = getStatus(row);
      const cfg    = STATUS_CONFIG[status];
      const author = row.author;
      const photo  = row?.checkin?.imageSelfie?.data || author?.photoURL || '';
      const name   = `${author?.firstName || ''} ${author?.lastName || ''}`.trim();
      const time   = row.checkin && row.checkin !== 'NA' && row.checkin.timeReported
        ? format(new Date(row.checkin.timeReported), 'hh:mm a') : '';

      const iconHtml = `
        <div style="position:relative;width:50px;height:50px;">
          <div style="width:50px;height:50px;border-radius:50%;border:3px solid ${cfg.color};box-shadow:0 4px 14px rgba(15,33,55,0.2),0 0 0 3px ${alpha(cfg.color, 0.2)};overflow:hidden;background:#f0f8f4;">
            <img src="${photo}" style="width:100%;height:100%;object-fit:cover;" onerror="this.style.display='none';" />
          </div>
          <div style="position:absolute;bottom:0;right:0;width:14px;height:14px;border-radius:50%;background:${cfg.color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.2);"></div>
        </div>`;

      const icon   = L.divIcon({ html: iconHtml, className: 'custom-marker', iconSize: [50, 50], iconAnchor: [25, 25], popupAnchor: [0, -30] });
      const marker = L.marker([coords.lat, coords.lng], { icon }).addTo(clusterGroup);

      const popupHtml = `
        <div style="padding:12px 14px;min-width:180px;">
          <div style="display:flex;align-items:center;gap:10px;">
            <img src="${photo}" style="width:38px;height:38px;border-radius:50%;border:2px solid ${cfg.color};object-fit:cover;flex-shrink:0;background:#f0f8f4;" onerror="this.style.background='#91ce0f';" />
            <div>
              <div style="font-weight:700;font-size:13px;color:#0f2137;">${name}</div>
              <div style="font-size:11px;color:#4a6a85;">${author?.role?.title || author?.username || ''}</div>
            </div>
          </div>
          <div style="margin-top:8px;padding-top:8px;border-top:1px solid #eef2f7;display:flex;justify-content:space-between;align-items:center;">
            <span style="font-size:11px;font-weight:700;color:${cfg.color};background:${cfg.bg};padding:3px 8px;border-radius:6px;border:1px solid ${cfg.border};">${cfg.label}</span>
            ${time ? `<span style="font-size:11px;color:#6b7f8e;">${time}</span>` : ''}
          </div>
          <div style="margin-top:7px;font-size:11px;color:#91ce0f;text-align:center;font-weight:600;cursor:pointer;">🗺 Click to view session trail</div>
        </div>`;

      marker.bindPopup(popupHtml, { maxWidth: 240, closeButton: false });
      marker.on('mouseover', () => marker.openPopup());
      marker.on('mouseout',  () => marker.closePopup());
      marker.on('click', () => { marker.closePopup(); onMarkerClickRef?.current?.(row); });

      markersRef.current[row._id] = marker;
      const authorId = row.author?._id ?? row.author?.id;
      if (authorId) userIdToRowIdRef.current[String(authorId)] = row._id;
      bounds.push([coords.lat, coords.lng]);
    });

    if (bounds.length > 0) {
      try { map.fitBounds(bounds, { padding: [60, 60], maxZoom: 14 }); } catch { }
    }
  }, [filtered, leafletReady]); 

  return {
    // DOM ref (attach to <div ref={mapRef}>)
    mapRef,
    // Stable refs the parent needs
    mapInstance, leafletRef, clusterGroupRef,
    markersRef, userIdToRowIdRef,
    trailLayersRef, trailPolylineRef, lastTrailDotRef, activityMarkersRef,
    activeTrailRowRef, trailRef,
    // State
    leafletReady,
    activeTrailRow, setActiveTrailRow,
    panelVisible, setPanelVisible,
    trail, setTrail,
    trailLoading, setTrailLoading,
    // Actions
    loadTrail,
    clearTrail,
    clearTrailLayers,
    clearActivityMarkers,
    setMarkersOpacity,
  };
}